/**
 * Color utilities for chart visualizations
 * Provides consistent color palettes across the dashboard
 */

// Vibrant color palette for primary chart elements
export const chartColors = [
  '#0070F3', // Primary blue
  '#FF4D4F', // Vibrant red
  '#7928CA', // Purple
  '#00C49F', // Teal
  '#FFBB28', // Amber
  '#FF7C43', // Orange
  '#F55A9C', // Pink
  '#10B981', // Green
  '#6366F1', // Indigo
  '#8884D8', // Lavender
  '#3F6FE1', // Royal blue
  '#F97316', // Deep orange
  '#06B6D4', // Cyan
  '#8B5CF6', // Violet
  '#EC4899', // Rose
  '#4ADE80', // Emerald
];

// Pastel color palette for secondary chart elements
export const pastelColors = [
  '#A7D8FD', // Light blue
  '#FFC0C0', // Light red
  '#D8B4FE', // Light purple
  '#A5F3D0', // Light teal
  '#FFE9A8', // Light amber
  '#FFCAB5', // Light orange
  '#FCB8D2', // Light pink
  '#A7F3D0', // Light green
  '#C7D2FE', // Light indigo
  '#D6D3FF', // Light lavender
];

// Gradient palette for area charts
export const gradientColors = [
  { start: '#0070F3', end: 'rgba(0, 112, 243, 0.1)' }, // Blue gradient
  { start: '#FF4D4F', end: 'rgba(255, 77, 79, 0.1)' }, // Red gradient
  { start: '#7928CA', end: 'rgba(121, 40, 202, 0.1)' }, // Purple gradient
  { start: '#00C49F', end: 'rgba(0, 196, 159, 0.1)' }, // Teal gradient
  { start: '#FFBB28', end: 'rgba(255, 187, 40, 0.1)' }, // Amber gradient
];

// Color schemes for specific data types
export const dataTypeColors = {
  ev: {
    primary: '#00C49F',
    secondary: '#A5F3D0',
    gradient: { start: '#00C49F', end: 'rgba(0, 196, 159, 0.1)' },
  },
  ice: {
    primary: '#FF7C43',
    secondary: '#FFCAB5',
    gradient: { start: '#FF7C43', end: 'rgba(255, 124, 67, 0.1)' },
  },
  combined: {
    primary: '#7928CA',
    secondary: '#D8B4FE',
    gradient: { start: '#7928CA', end: 'rgba(121, 40, 202, 0.1)' },
  }
};

// Color schemes for specific makers (consistent colors across visualizations)
export const makerColors = {
  'TATA MOTORS': '#2563EB',
  'MAHINDRA': '#9333EA',
  'MARUTI': '#F97316',
  'HYUNDAI': '#0EA5E9',
  'KIA': '#EF4444',
  'TOYOTA': '#10B981',
  'HONDA': '#EC4899',
  'MG MOTOR': '#0891B2',
  'BMW': '#0284C7',
  'AUDI': '#9F1239',
  'MERCEDES-BENZ': '#737373'
};

// Get a color for a maker, with consistent mapping
export const getMakerColor = (makerName, index = 0) => {
  if (makerColors[makerName]) {
    return makerColors[makerName];
  }
  // Fallback to the chart colors, cycling through the array
  return chartColors[index % chartColors.length];
};

// Generate an array of colors for a dataset
export const getColorArray = (count, palette = chartColors) => {
  const colors = [];
  for (let i = 0; i < count; i++) {
    colors.push(palette[i % palette.length]);
  }
  return colors;
};

// Generate color with opacity
export const getColorWithOpacity = (color, opacity = 0.7) => {
  // If color is in hex format, convert to rgba
  if (color.startsWith('#')) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  // If already in rgba format, just change opacity
  if (color.startsWith('rgb')) {
    return color.replace(/rgba?\((.+?)\)/, `rgba($1, ${opacity})`);
  }
  return color;
};

// Get a complementary color for text on a background
export const getTextColorForBackground = (backgroundColor) => {
  // Simple luminance calculation to determine if black or white text is better
  if (backgroundColor.startsWith('#')) {
    const r = parseInt(backgroundColor.slice(1, 3), 16);
    const g = parseInt(backgroundColor.slice(3, 5), 16);
    const b = parseInt(backgroundColor.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  }
  return '#000000'; // Default to black
}; 